/**
 * useLinearExportState — manages Linear-specific selection state for export.
 *
 * Handles:
 * - Team selection
 * - Project selection (cascade: loads when team changes)
 * - Data loading callback for useExportOAuth
 */
import { useState, useCallback, useEffect } from 'react';
import { toUserFacingMessage } from '../../../userFacingError.js';
export function useLinearExportState(runtime) {
    const { api: integrationsApi, debug } = runtime;
    const [linearTeams, setLinearTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [linearProjects, setLinearProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [projectsLoading, setProjectsLoading] = useState(false);
    // A failed projects fetch used to say nothing, and because the Project field
    // only renders when the list is non-empty, the whole field silently vanished
    // (EP-099 sibling; milder than Asana's because a Linear project is optional).
    const [projectsError, setProjectsError] = useState(null);
    const [projectsReloadNonce, setProjectsReloadNonce] = useState(0);
    /** Callback for useExportOAuth to populate Linear data */
    const onLinearDataLoaded = useCallback((teams) => {
        debug.log('Linear teams loaded:', teams.length);
        setLinearTeams(teams);
        if (teams.length > 0) {
            setSelectedTeamId(teams[0].id);
        }
    }, [runtime]);
    /** Load projects when selected team changes */
    useEffect(() => {
        if (!selectedTeamId) {
            setLinearProjects([]);
            setSelectedProjectId('');
            setProjectsError(null);
            return;
        }
        let cancelled = false;
        setProjectsLoading(true);
        setProjectsError(null);
        integrationsApi.getLinearProjects(selectedTeamId)
            .then((projects) => {
            if (cancelled)
                return;
            debug.log('Linear projects loaded:', projects.length);
            setLinearProjects(projects);
            setSelectedProjectId('');
            setProjectsError(null);
            setProjectsLoading(false);
        })
            .catch((err) => {
            if (cancelled)
                return;
            debug.error('Failed to load projects:', err);
            setLinearProjects([]);
            setSelectedProjectId('');
            setProjectsError(toUserFacingMessage(err, "Couldn't load projects for this team."));
            setProjectsLoading(false);
        });
        return () => { cancelled = true; };
    }, [selectedTeamId, projectsReloadNonce, runtime]);
    /** Refetch the projects for the current team after a failure. */
    const retryLoadProjects = useCallback(() => {
        setProjectsReloadNonce((n) => n + 1);
    }, [runtime]);
    /** Reset Linear-specific state */
    const resetLinearState = useCallback(() => {
        debug.log('Resetting Linear state');
        setSelectedTeamId('');
        setLinearProjects([]);
        setSelectedProjectId('');
        setProjectsError(null);
    }, [runtime]);
    return {
        linearTeams,
        selectedTeamId,
        setSelectedTeamId,
        linearProjects,
        selectedProjectId,
        setSelectedProjectId,
        projectsLoading,
        projectsError,
        retryLoadProjects,
        onLinearDataLoaded,
        resetLinearState,
    };
}
//# sourceMappingURL=useLinearExportState.js.map